import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, MapPinned, Upload } from 'lucide-react';
import { formatDate } from '../utils/helpers';

export function PlanClientStatusAvailable({ planCount = 0, lastUploadedAt }) {
  const countLabel =
    planCount > 0
      ? `${planCount} plan${planCount > 1 ? 's' : ''}`
      : 'Plan(s) disponible(s)';

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 420, damping: 30 }}
      className="relative overflow-hidden rounded-xl border border-emerald-200/90 bg-gradient-to-br from-emerald-50/95 via-white to-teal-50/80 p-3.5 shadow-sm ring-1 ring-emerald-100/50"
    >
      <motion.div
        aria-hidden
        className="pointer-events-none absolute inset-y-0 w-1/2 bg-gradient-to-r from-transparent via-white/70 to-transparent"
        animate={{ left: ['-50%', '120%'] }}
        transition={{
          duration: 2.4,
          repeat: Infinity,
          ease: 'linear',
          repeatDelay: 4,
        }}
      />
      <div className="relative flex items-start gap-3">
        <div className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 shadow-md shadow-emerald-500/25">
          <MapPinned className="h-5 w-5 text-white" strokeWidth={2.25} />
          <span className="absolute -right-0.5 -top-0.5 flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-white" />
          </span>
        </div>
        <div className="min-w-0 flex-1 pt-0.5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-bold tracking-tight text-emerald-950">Plans actifs</span>
            <motion.span
              initial={{ scale: 0.85, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.12, type: 'spring', stiffness: 500 }}
              className="inline-flex items-center rounded-full border border-emerald-200/80 bg-white/80 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-emerald-700 shadow-sm"
            >
              {countLabel}
            </motion.span>
          </div>
          {lastUploadedAt ? (
            <p className="mt-1.5 flex items-center gap-1.5 text-xs text-emerald-800/85">
              <Calendar className="h-3.5 w-3.5 shrink-0 text-emerald-600" aria-hidden />
              <span>
                Dernier dépôt ·{' '}
                <span className="font-semibold text-emerald-900">
                  {formatDate(lastUploadedAt)}
                </span>
              </span>
            </p>
          ) : (
            <p className="mt-1.5 text-xs text-emerald-700/80">Prêt pour le placement sur plan</p>
          )}
        </div>
      </div>
    </motion.div>
  );
}

export function PlanClientStatusEmpty() {
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 420, damping: 30 }}
      whileHover={{ scale: 1.012 }}
      className="group relative overflow-hidden rounded-xl border-2 border-dashed border-slate-200/90 bg-gradient-to-br from-slate-50 via-white to-gray-50 p-3.5 transition-[border-color,box-shadow] duration-300 hover:border-amber-300/80 hover:shadow-sm"
    >
      <motion.div
        aria-hidden
        className="pointer-events-none absolute -inset-1 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
        style={{
          background:
            'radial-gradient(ellipse 80% 60% at 20% 50%, rgba(251, 191, 36, 0.12), transparent 65%)',
        }}
        animate={{ opacity: [0.3, 0.5, 0.3] }}
        transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
      />
      <div className="relative flex items-center gap-3">
        <motion.div
          animate={{ y: [0, -4, 0] }}
          transition={{ duration: 2.8, repeat: Infinity, ease: 'easeInOut' }}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100/90 ring-1 ring-slate-200/90 transition-colors duration-300 group-hover:bg-amber-50 group-hover:ring-amber-200/90"
        >
          <Upload
            className="h-5 w-5 text-slate-400 transition-colors duration-300 group-hover:text-amber-600"
            strokeWidth={2.25}
          />
        </motion.div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-600 transition-colors duration-300 group-hover:text-slate-800">
            Aucun plan téléchargé
          </p>
          <p className="mt-0.5 text-xs leading-snug text-slate-400 transition-colors duration-300 group-hover:text-slate-500">
            Importez un plan avec{' '}
            <span className="font-semibold text-slate-500 group-hover:text-amber-700/90">
              Gérer plans
            </span>
          </p>
        </div>
      </div>
    </motion.div>
  );
}
